import { useState, useEffect, useRef, useMemo } from 'react';

// Shared inventory count input. On mobile it's an iOS-style scroll-wheel bottom
// sheet (tap to open, spin or type); on desktop it's a plain number input.
// Used across Spirits, Coffee, and Taproom inventory. Supports decimals (step),
// and optional clearing (Taproom leaves blanks).

const WHEEL_ITEM_H = 44;
const WHEEL_VISIBLE = 5;

function decimalsOf(step) {
  const s = String(step);
  return s.includes('.') ? s.split('.')[1].length : 0;
}
function fmtNum(v, dec) {
  if (v == null || v === '' || !Number.isFinite(Number(v))) return '';
  const n = Number(v);
  return dec > 0 ? String(+n.toFixed(dec)) : String(Math.round(n));
}

function WheelSheet({ initial, min, max, step, allowDecimal, allowClear, title, subtitle, onCancel, onClear, onDone }) {
  const dec = allowDecimal ? Math.max(1, decimalsOf(step)) : 0;
  const count = Math.max(1, Math.round((max - min) / step) + 1);
  const valueAt = (i) => +(min + i * step).toFixed(dec);
  const indexOf = (v) => Math.round((v - min) / step);

  const scrollRef = useRef(null);
  const [selected, setSelected] = useState(initial);
  const [text, setText] = useState(fmtNum(initial, dec));
  const lastVibrated = useRef(initial);

  const values = useMemo(() => Array.from({ length: count }, (_, i) => valueAt(i)), [count]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = indexOf(initial) * WHEEL_ITEM_H;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.min(count - 1, Math.max(0, Math.round(el.scrollTop / WHEEL_ITEM_H)));
    const v = valueAt(idx);
    if (v !== selected) { setSelected(v); setText(fmtNum(v, dec)); }
    if (v !== lastVibrated.current) { lastVibrated.current = v; if (navigator.vibrate) navigator.vibrate(4); }
  };

  const handleTextChange = (e) => {
    let t = e.target.value;
    t = allowDecimal ? t.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1') : t.replace(/[^\d]/g, '');
    setText(t);
    if (t !== '' && t !== '.') setSelected(Math.min(max, Math.max(min, parseFloat(t))));
  };
  const handleTextBlur = () => {
    const n = (text === '' || text === '.') ? min : Math.min(max, Math.max(min, parseFloat(text) || min));
    setSelected(n); setText(fmtNum(n, dec));
    if (scrollRef.current) scrollRef.current.scrollTop = indexOf(n) * WHEEL_ITEM_H;
  };

  const pad = ((WHEEL_VISIBLE - 1) / 2) * WHEEL_ITEM_H;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={() => onDone(selected)} />
      <div className="relative w-full sm:max-w-sm bg-gray-800 border-t sm:border border-gray-700 rounded-t-2xl sm:rounded-2xl p-4 pb-6 animate-[fadeIn_0.15s_ease-out]">
        <div className="text-center mb-3">
          {subtitle && <p className="text-gray-400 text-xs truncate">{subtitle}</p>}
          <p className="text-cream font-semibold mb-2">{title}</p>
          <input
            type="text" inputMode={allowDecimal ? 'decimal' : 'numeric'}
            value={text} onChange={handleTextChange} onBlur={handleTextBlur}
            onFocus={e => e.target.select()}
            onKeyDown={e => { if (e.key === 'Enter') { e.currentTarget.blur(); onDone(selected); } }}
            aria-label={`${title} — type a number`}
            className="w-24 mx-auto block text-center bg-gray-900 border border-gray-600 rounded-lg py-1.5 text-white text-xl font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <p className="text-gray-500 text-[10px] mt-1">type or spin</p>
        </div>

        <div className="relative mx-auto max-w-[220px]" style={{ height: WHEEL_VISIBLE * WHEEL_ITEM_H }}>
          <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 rounded-lg bg-gray-700/40 border-y border-gray-600" style={{ height: WHEEL_ITEM_H }} />
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-gray-800 to-transparent" style={{ height: pad }} />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-gray-800 to-transparent" style={{ height: pad }} />
          <div ref={scrollRef} onScroll={handleScroll} className="no-scrollbar h-full overflow-y-scroll"
            style={{ scrollSnapType: 'y mandatory', WebkitOverflowScrolling: 'touch' }}>
            <div style={{ height: pad }} />
            {values.map(n => (
              <div key={n} onClick={() => scrollRef.current?.scrollTo({ top: indexOf(n) * WHEEL_ITEM_H, behavior: 'smooth' })}
                className={`flex items-center justify-center tabular-nums transition-all duration-100 ${n === selected ? 'text-white text-3xl font-bold' : 'text-gray-500 text-xl'}`}
                style={{ height: WHEEL_ITEM_H, scrollSnapAlign: 'center' }}>
                {fmtNum(n, dec)}
              </div>
            ))}
            <div style={{ height: pad }} />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button onClick={onCancel} className="flex-1 py-2.5 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-600 transition">Cancel</button>
          {allowClear && <button onClick={onClear} className="px-4 py-2.5 bg-gray-700 text-gray-300 text-sm rounded-lg hover:bg-gray-600 transition">Clear</button>}
          <button onClick={() => onDone(selected)} className="flex-1 py-2.5 text-white text-sm rounded-lg font-semibold transition" style={{ backgroundColor: '#F05A28' }}>Done</button>
        </div>
      </div>
    </div>
  );
}

// Mobile wheel button (opens the sheet).
function WheelButton({ value, onSave, disabled, label, itemName, step, allowDecimal, allowClear, min, max, className }) {
  const dec = allowDecimal ? Math.max(1, decimalsOf(step)) : 0;
  const [open, setOpen] = useState(false);
  const current = (value == null || value === '') ? null : Number(value);
  const resolvedMax = max ?? Math.max(150, Math.ceil(current || 0) + 50);

  const commit = async (n) => { setOpen(false); if (current != null && n === current) return; await onSave(n); };
  const clear = async () => { setOpen(false); if (current == null) return; await onSave(null); };

  return (
    <div className={className}>
      <button type="button" disabled={disabled}
        onClick={() => { if (navigator.vibrate) navigator.vibrate(8); setOpen(true); }}
        className={`w-full flex items-center justify-end gap-1.5 rounded-lg border px-3 py-2 text-lg font-semibold tabular-nums transition disabled:opacity-50 ${
          current == null ? 'bg-gray-700 border-gray-600 text-gray-500' : 'bg-gray-700 border-gray-500 text-white'}`}>
        {current == null ? '–' : fmtNum(current, dec)}
        <svg className="w-3.5 h-3.5 text-gray-500" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M7 8l3-3 3 3M7 12l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <WheelSheet initial={current ?? min} min={min} max={resolvedMax} step={step} allowDecimal={allowDecimal}
          allowClear={allowClear} title={label} subtitle={itemName}
          onCancel={() => setOpen(false)} onClear={clear} onDone={commit} />
      )}
    </div>
  );
}

// Desktop plain number input (save on blur / Enter).
function PlainInput({ value, onSave, disabled, step, allowDecimal, className }) {
  const dec = allowDecimal ? Math.max(1, decimalsOf(step)) : 0;
  const [val, setVal] = useState(fmtNum(value, dec));
  const [saving, setSaving] = useState(false);
  const initial = useRef(fmtNum(value, dec));

  useEffect(() => {
    if (!saving) { const inc = fmtNum(value, dec); setVal(inc); initial.current = inc; }
  }, [value, saving, dec]);

  const commit = async () => {
    if (val === initial.current) return;
    if (val === '' || isNaN(parseFloat(val))) { setVal(initial.current); return; }
    setSaving(true);
    try {
      const parsed = allowDecimal ? parseFloat(val) : parseInt(val, 10);
      await onSave(parsed);
      initial.current = fmtNum(parsed, dec); setVal(fmtNum(parsed, dec));
    } finally { setSaving(false); }
  };

  return (
    <input type="number" min="0" step={allowDecimal ? 'any' : 1} inputMode={allowDecimal ? 'decimal' : 'numeric'}
      value={val} disabled={disabled} placeholder="–"
      onChange={e => setVal(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className={className || 'w-20 bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm text-right focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50'} />
  );
}

// Responsive: wheel on mobile, plain input on desktop.
export default function WheelCountCell({
  value, onSave, disabled, label, itemName,
  step = 1, allowDecimal = false, allowClear = false, min = 0, max,
  desktopInputClassName,
}) {
  return (
    <>
      <WheelButton className="sm:hidden" value={value} onSave={onSave} disabled={disabled}
        label={label} itemName={itemName} step={step} allowDecimal={allowDecimal} allowClear={allowClear} min={min} max={max} />
      <PlainInput className={`hidden sm:block ${desktopInputClassName || 'w-20 bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm text-right focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50'}`}
        value={value} onSave={onSave} disabled={disabled} step={step} allowDecimal={allowDecimal} />
    </>
  );
}

export { WheelSheet };
